import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import Confirm, { type ConfirmConfig } from '../commons/Confirm';
import ProfaseModal from '../commons/ProfaseModal';
import CategoriaActividadAutocomplete from '../shared/categoria-actividad-autocomplete';
import {
  type ActividadAdicionalFormPayload,
  actividadAdicionalFormFromTask,
  resolveCategoriaActividadLabel,
  validateActividadAdicionalForm,
} from '../shared/workflow/actividad-adicional';
import ActividadAdjuntosSection from './ActividadAdjuntosSection';

export interface ActividadAdicionalTaskContext {
  asunto: string;
  empresa?: string;
  workflow?: string;
  periodo?: string;
}

interface ActividadAdicionalModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  taskContext: ActividadAdicionalTaskContext | null;
  editTaskRaw?: unknown;
  editActividadId?: string | number | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: ActividadAdicionalFormPayload) => Promise<void>;
}

const emptyForm = (): ActividadAdicionalFormPayload => ({
  actividadId: '',
  descripcion: '',
  fechaVencimientoProfase: '',
  requiereArchivo: false,
});

export default function ActividadAdicionalModal({
  open,
  mode,
  taskContext,
  editTaskRaw,
  editActividadId: editActividadIdProp,
  saving,
  onClose,
  onSubmit,
}: ActividadAdicionalModalProps) {
  const [form, setForm] = useState<ActividadAdicionalFormPayload>(emptyForm);
  const [actividadError, setActividadError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adjuntosConfirm, setAdjuntosConfirm] = useState<ConfirmConfig | null>(null);
  const isEdit = mode === 'edit';

  const actividadInitialLabel = useMemo(
    () => (isEdit && editTaskRaw ? resolveCategoriaActividadLabel(editTaskRaw) : ''),
    [isEdit, editTaskRaw],
  );

  const editActividadId = useMemo(() => {
    if (editActividadIdProp != null && editActividadIdProp !== '') return editActividadIdProp;
    if (!isEdit || !editTaskRaw || typeof editTaskRaw !== 'object') return null;
    const raw = editTaskRaw as { id?: unknown };
    const id = raw.id;
    if (typeof id === 'number' && Number.isFinite(id)) return id;
    if (typeof id === 'string' && id.trim()) return id.trim();
    return null;
  }, [editActividadIdProp, isEdit, editTaskRaw]);

  useEffect(() => {
    if (!open) return;
    setForm(isEdit && editTaskRaw ? actividadAdicionalFormFromTask(editTaskRaw) : emptyForm());
    setActividadError(false);
    setError(null);
  }, [open, isEdit, editTaskRaw, taskContext?.asunto]);

  if (!open || !taskContext) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateActividadAdicionalForm(form);
    if (validationError) {
      setActividadError(!form.actividadId.trim());
      setError(validationError);
      return;
    }
    setActividadError(false);
    setError(null);
    try {
      await onSubmit(form);
    } catch (err: unknown) {
      const fallback = isEdit
        ? 'No se pudo actualizar la actividad adicional.'
        : 'No se pudo crear la actividad adicional.';
      setError(err instanceof Error ? err.message : fallback);
    }
  };

  return (
    <ProfaseModal
      open={open}
      size="default"
      dialogClassName="profase-modal-dialog--actividad-adicional"
      titleId="actividad-adicional-modal-title"
      onBackdropMouseDown={e => {
        if (saving) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form className="actividad-adicional-modal" onSubmit={handleSubmit}>
        <div className="actividad-adicional-modal__header">
          <h2 id="actividad-adicional-modal-title" className="profase-modal-title">
            {isEdit ? 'Editar actividad adicional' : 'Nueva actividad adicional'}
          </h2>
          <button
            type="button"
            className="actividad-adicional-modal__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Cerrar"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {adjuntosConfirm ? (
          <div className="actividad-adicional-modal__confirm">
            <Confirm config={adjuntosConfirm} />
          </div>
        ) : null}

        {!isEdit && (
          <div className="actividad-adicional-modal__context">
            <p className="actividad-adicional-modal__context-label">Tarea origen</p>
            <p className="actividad-adicional-modal__context-value">{taskContext.asunto}</p>
            {taskContext.periodo && (
              <p className="actividad-adicional-modal__context-meta">Periodo: {taskContext.periodo}</p>
            )}
            {taskContext.workflow && (
              <p className="actividad-adicional-modal__context-meta">Workflow: {taskContext.workflow}</p>
            )}
            {taskContext.empresa && (
              <p className="actividad-adicional-modal__context-meta">Empresa: {taskContext.empresa}</p>
            )}
          </div>
        )}

        <div className="actividad-adicional-modal__field">
          <label htmlFor="actividad-adicional-actividad">Actividad</label>
          <CategoriaActividadAutocomplete
            value={form.actividadId}
            initialLabel={actividadInitialLabel}
            disabled={saving}
            hasError={actividadError}
            placeholder="Buscar actividad del catálogo…"
            onChange={actividadId => {
              setActividadError(false);
              setForm(prev => ({ ...prev, actividadId }));
            }}
          />
        </div>

        <div className="actividad-adicional-modal__field">
          <label htmlFor="actividad-adicional-descripcion">Descripción</label>
          <textarea
            id="actividad-adicional-descripcion"
            value={form.descripcion}
            rows={4}
            disabled={saving}
            placeholder="Detalle específico del caso"
            onChange={e => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
          />
          <span className="actividad-adicional-modal__field-hint">
            Contexto libre de esta actividad adicional (no confundir con el catálogo).
          </span>
        </div>

        <div className="actividad-adicional-modal__field">
          <label htmlFor="actividad-adicional-vencimiento-profase">Vencimiento Profase</label>
          <input
            id="actividad-adicional-vencimiento-profase"
            type="date"
            value={form.fechaVencimientoProfase}
            disabled={saving}
            onChange={e => setForm(prev => ({ ...prev, fechaVencimientoProfase: e.target.value }))}
          />
          <span className="actividad-adicional-modal__field-hint">
            Opcional al crear; necesaria para poder finalizar la actividad. El vencimiento SAT lo calcula el sistema.
          </span>
        </div>

        <label className="actividad-adicional-modal__checkbox">
          <input
            type="checkbox"
            checked={form.requiereArchivo}
            disabled={saving}
            onChange={e => setForm(prev => ({ ...prev, requiereArchivo: e.target.checked }))}
          />
          Requiere archivo adjunto
        </label>

        {isEdit && editActividadId != null ? (
          <ActividadAdjuntosSection
            actividadId={editActividadId}
            disabled={saving}
            requiereArchivo={form.requiereArchivo}
            onConfirmConfigChange={setAdjuntosConfirm}
          />
        ) : !isEdit ? (
          <p className="actividad-adicional-modal__field-hint">
            Podrá agregar archivos adjuntos después de crear la actividad.
          </p>
        ) : null}

        {error && <p className="actividad-adicional-modal__error">{error}</p>}

        <div className="actividad-adicional-modal__actions">
          <button type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" disabled={saving}>
            {saving
              ? (isEdit ? 'Guardando…' : 'Creando…')
              : (isEdit ? 'Guardar cambios' : 'Crear actividad')}
          </button>
        </div>
      </form>
    </ProfaseModal>
  );
}
